import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from "react";
import {
    createMeetingSession as apiCreateMeetingSession,
    getMeetingSessions as apiGetMeetingSessions,
    getMeetingSessionById as apiGetMeetingSessionById,
    updateMeetingSession as apiUpdateMeetingSession,
    deleteMeetingSession as apiDeleteMeetingSession,
    uploadMeetingChunk as apiUploadMeetingChunk,
    finalizeMeetingSession as apiFinalizeMeetingSession,
    getMeetingDownloadUrl as apiGetMeetingDownloadUrl,
    getTranscriptionStatus as apiGetTranscriptionStatus,
    getTranscription as apiGetTranscription,
    updateSpeakerMapping as apiUpdateSpeakerMapping,
    retryMeetingTranscription as apiRetryMeetingTranscription,
} from "../service/apiService";
import { toast } from "sonner";
import { useUser } from "./UserContext";
import {
    MeetingSession,
    SpeakerMapping,
    Transcription,
    PaginationData,
    TranscriptionStatusResponse,
} from "../types";
import MeetingAudioRecorder, { AudioChunk } from "../utils/MeetingAudioRecorder";
import { transcriptStreamClient } from "../utils/TranscriptStreamClient";
import {
    addMeetingNotification,
    updateMeetingNotificationStatus,
    getMeetingNotifications,
    removeMeetingNotification,
} from "../helpers/meetingNotificationLocalStorage";

const CHUNK_INTERVAL_MS = 30000; // 30 seconds
const CHUNK_RETRY_INTERVAL_MS = 10000; // retry cadence for failed chunk uploads
// Continuous silence before the "are you still there?" warning. Long
// enough that a break in a therapy session doesn't end the recording.
// The recorder re-arms only after sound returns, so a dismissed warning
// doesn't nag through an intentionally quiet stretch.
export const SILENCE_WARNING_MS = 20 * 60 * 1000; // 20 minutes
const NOTIFICATION_POLL_INTERVAL_MS = 30000;
// A session stuck in processing/transcribing this long is treated as
// failed (same threshold the views use for the Retry affordance) — the
// bell poll must not keep hitting the API for it forever.
const NOTIFICATION_MAX_AGE_MS = 3 * 60 * 60 * 1000;

export type MeetingRecordingState =
    | 'idle'
    | 'starting'
    | 'recording'
    | 'paused'
    | 'stopping'
    | 'finalizing'
    // Finalize failed after capture already stopped. The session and its
    // uploaded chunks are intact server-side; stopRecording can be
    // called again to retry, and startRecording refuses until the user
    // retries or discards — going back to 'idle' here orphaned sessions.
    | 'finalize_failed';

interface PendingChunk {
    chunkIndex: number;
    blob: Blob;
    attempts: number;
}

interface ErrorState {
    message: string;
    code?: number;
}

interface MeetingContextProps {
    // State
    sessions: MeetingSession[];
    selectedSession: MeetingSession | null;
    loading: boolean;
    error: ErrorState | null;
    pagination: PaginationData;

    // Actions
    setSelectedSession: (session: MeetingSession | null) => void;
    fetchSessions: (page?: number, itemsPerPage?: number) => Promise<void>;
    createSession: (title?: string, description?: string) => Promise<string | null>;
    updateSession: (sessionId: string, updates: { title?: string; description?: string }) => Promise<void>;
    deleteSession: (sessionId: string) => Promise<void>;

    // Transcription
    pollTranscriptionStatus: (sessionId: string) => Promise<TranscriptionStatusResponse>;
    getTranscription: (sessionId: string) => Promise<Transcription | null>;

    // Speaker mapping
    updateSpeakerMapping: (sessionId: string, mappings: SpeakerMapping[]) => Promise<void>;

    // Download
    getDownloadUrl: (sessionId: string) => Promise<string | null>;

    // Utilities
    setPage: (page: number) => void;
    resetError: () => void;
    refreshSession: (sessionId: string) => Promise<void>;
    // regenerate=true re-transcribes a COMPLETED session from its audio
    retryTranscription: (sessionId: string, regenerate?: boolean) => Promise<void>;
}

// Fast-changing recording state lives in its own context: while a
// recording runs, recordingDuration ticks every second — only the
// recorder UI should re-render for that, not every meetings consumer.
interface MeetingRecordingContextProps {
    isRecording: boolean;
    recordingState: MeetingRecordingState;
    currentSessionId: string | null;
    recordingDuration: number;
    uploadedChunks: number;
    // Chunks that failed to upload and are queued for retry
    pendingUploadCount: number;
    // SILENCE_WARNING_MS of continuous silence detected — the recorder UI shows a
    // stop-countdown dialog the user can cancel
    silenceWarning: boolean;
    // The user asked for system audio but only the mic is on the bus
    // (picker cancelled, or the share carried no audio track). The
    // remote party is not being recorded, and silence detection cannot
    // hear them — so the recorder UI must say so and must NOT auto-stop
    // on silence.
    systemAudioUnavailable: boolean;
    // The microphone stopped mid-recording (headset unplugged, device
    // switched, permission revoked). Your own voice is no longer being
    // captured — nothing else in the UI would say so, because frames
    // keep flowing as silence.
    micUnavailable: boolean;

    startRecording: (title?: string, includeSystemAudio?: boolean) => Promise<string | null>;
    dismissSilenceWarning: () => void;
    pauseRecording: () => Promise<void>;
    resumeRecording: () => Promise<void>;
    stopRecording: (durationSeconds: number) => Promise<boolean>;
    discardRecording: () => Promise<void>;
}

// Module-scoped mirror of the recording state, for readers that need
// the value at one instant but must NOT subscribe: the recording
// context re-renders every second while the duration ticks, and the
// sidebar (which only asks "is a recording running?" when the user
// closes the meetings modal) would re-render with it.
let recordingStateSnapshot: MeetingRecordingState = 'idle';

export const getMeetingRecordingState = (): MeetingRecordingState =>
    recordingStateSnapshot;

const MeetingContext = createContext<MeetingContextProps | undefined>(undefined);
const MeetingRecordingContext = createContext<MeetingRecordingContextProps | undefined>(undefined);

export const useMeeting = (): MeetingContextProps => {
    const context = useContext(MeetingContext);
    if (!context) {
        throw new Error("useMeeting must be used within a MeetingProvider");
    }
    return context;
};

export const useMeetingRecording = (): MeetingRecordingContextProps => {
    const context = useContext(MeetingRecordingContext);
    if (!context) {
        throw new Error("useMeetingRecording must be used within a MeetingProvider");
    }
    return context;
};

interface MeetingProviderProps {
    children: React.ReactNode;
}

export const MeetingProvider: React.FC<MeetingProviderProps> = ({ children }) => {
    const [sessions, setSessions] = useState<MeetingSession[]>([]);
    const [selectedSession, setSelectedSession] = useState<MeetingSession | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ErrorState | null>(null);
    const [pagination, setPagination] = useState<PaginationData>({
        page: 1,
        items_per_page: 10,
        total_pages: 1,
        has_next: false,
        next_page: null,
    });

    // Recording state
    const [isRecording, setIsRecording] = useState(false);
    const [recordingState, setRecordingState] = useState<MeetingRecordingState>('idle');
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [uploadedChunks, setUploadedChunks] = useState(0);
    const [pendingUploadCount, setPendingUploadCount] = useState(0);
    const [silenceWarning, setSilenceWarning] = useState(false);
    const [systemAudioUnavailable, setSystemAudioUnavailable] = useState(false);
    const [micUnavailable, setMicUnavailable] = useState(false);

    // Audio Recorder Ref
    const recorderRef = useRef<MeetingAudioRecorder | null>(null);
    const startTimeRef = useRef<number>(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // Refs for closure-safe access
    const sessionIdRef = useRef<string | null>(null);

    // Synchronous mirror of recordingState — guards start/stop against
    // double-clicks that land before React re-renders.
    const recordingStateRef = useRef<MeetingRecordingState>('idle');
    const setRecState = useCallback((state: MeetingRecordingState) => {
        recordingStateRef.current = state;
        recordingStateSnapshot = state;
        setRecordingState(state);
    }, []);

    // Recorded (active) time, excluding pauses. The displayed duration
    // and the finalized duration both come from these so they match the
    // actual audio length.
    const accumulatedActiveMsRef = useRef<number>(0);
    const activeSegmentStartRef = useRef<number>(0);

    const getActiveDurationMs = useCallback((): number => {
        const running = recordingStateRef.current === 'recording'
            ? Date.now() - activeSegmentStartRef.current
            : 0;
        return accumulatedActiveMsRef.current + running;
    }, []);

    const startDurationTimer = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setRecordingDuration(Math.floor(getActiveDurationMs() / 1000));
        }, 1000);
    }, [getActiveDurationMs]);

    const stopDurationTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    // Failed chunk uploads queued for retry (audio is already gone from
    // the recorder's buffer, so losing these means losing audio).
    const pendingChunksRef = useRef<PendingChunk[]>([]);
    // Timer-fired uploads still on the wire. stopRecording awaits these
    // before finalize — finalizing while one is in flight makes the
    // server's S3 listing miss that chunk and 409 the finalize.
    const inFlightUploadsRef = useRef<Set<Promise<void>>>(new Set());
    const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
    const retryInFlightRef = useRef(false);

    const { userId } = useUser();

    const resetError = useCallback(() => {
        setError(null);
    }, []);

    // The recorder's silence flag stays latched until sound returns, so
    // dismissing here won't be immediately re-triggered by the ongoing
    // silence.
    const dismissSilenceWarning = useCallback(() => {
        setSilenceWarning(false);
    }, []);

    // Fetch paginated sessions
    const fetchSessions = useCallback(async (
        page: number = 1,
        itemsPerPage: number = 10
    ) => {
        if (!userId) return;

        try {
            setLoading(true);
            setError(null);

            const response = await apiGetMeetingSessions(page, itemsPerPage);

            if (response && response.sessions) {
                setSessions(response.sessions);
                setPagination(response.pagination);
            } else {
                setSessions([]);
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch meeting sessions';
            console.error("Failed to fetch meeting sessions:", err);
            setError({ message: errorMessage });
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    // Refresh a single session. Functional updates keep this callback's
    // identity stable — it feeds effect deps in several views.
    const refreshSession = useCallback(async (sessionId: string) => {
        try {
            const session = await apiGetMeetingSessionById(sessionId);

            // Update in sessions list
            setSessions(prev => prev.map(s =>
                s.id === sessionId ? session : s
            ));

            // Update selected session if it matches
            setSelectedSession(prev => prev?.id === sessionId ? session : prev);
        } catch (err) {
            console.error("Failed to refresh session:", err);
        }
    }, []);

    // Create a new session
    const createSession = useCallback(async (
        title?: string,
        description?: string
    ): Promise<string | null> => {
        try {
            setLoading(true);
            setError(null);

            const response = await apiCreateMeetingSession(title, description);

            if (response && response.id) {
                // Refresh sessions list
                await fetchSessions(pagination.page, pagination.items_per_page);
                toast.success("Meeting session created!");
                return response.id;
            }

            return null;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to create meeting session';
            console.error("Failed to create meeting session:", err);
            setError({ message: errorMessage });
            toast.error(errorMessage);
            return null;
        } finally {
            setLoading(false);
        }
    }, [fetchSessions, pagination.page, pagination.items_per_page]);

    // Update a session
    const updateSession = useCallback(async (
        sessionId: string,
        updates: { title?: string; description?: string }
    ) => {
        try {
            setLoading(true);
            setError(null);

            await apiUpdateMeetingSession(sessionId, updates);

            // Update local state by merging updates with existing session
            setSessions(prev => prev.map(s =>
                s.id === sessionId ? { ...s, ...updates } : s
            ));

            setSelectedSession(prev => prev?.id === sessionId ? { ...prev, ...updates } : prev);

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update meeting session';
            console.error("Failed to update meeting session:", err);
            setError({ message: errorMessage });
            toast.error(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Delete a session
    const deleteSession = useCallback(async (sessionId: string) => {
        try {
            setLoading(true);
            setError(null);

            await apiDeleteMeetingSession(sessionId);

            // Update local state
            setSessions(prev => prev.filter(s => s.id !== sessionId));
            setSelectedSession(prev => prev?.id === sessionId ? null : prev);

            toast.success("Meeting session deleted!");
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete meeting session';
            console.error("Failed to delete meeting session:", err);
            setError({ message: errorMessage });
            toast.error(errorMessage);
            // Rethrow so callers can react (e.g. keep a confirm dialog
            // open) instead of proceeding as if the delete succeeded.
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const stopRetryTimer = useCallback(() => {
        if (retryTimerRef.current) {
            clearInterval(retryTimerRef.current);
            retryTimerRef.current = null;
        }
    }, []);

    // Attempt to upload every queued chunk. A chunk that fails again
    // stays in the queue; the recorder's buffer is long gone, so
    // dropping it would permanently lose that audio.
    const flushPendingChunks = useCallback(async (sessionId: string): Promise<number> => {
        if (retryInFlightRef.current) return pendingChunksRef.current.length;
        retryInFlightRef.current = true;
        try {
            for (const pending of [...pendingChunksRef.current]) {
                try {
                    const response = await apiUploadMeetingChunk(sessionId, pending.chunkIndex, pending.blob);
                    if (response.uploaded) {
                        pendingChunksRef.current = pendingChunksRef.current.filter(
                            p => p.chunkIndex !== pending.chunkIndex
                        );
                        setUploadedChunks(response.total_uploaded);
                    }
                } catch (err) {
                    pending.attempts += 1;
                    console.error(`[MeetingContext] Retry ${pending.attempts} failed for chunk ${pending.chunkIndex}:`, err);
                }
            }
        } finally {
            retryInFlightRef.current = false;
            setPendingUploadCount(pendingChunksRef.current.length);
            if (pendingChunksRef.current.length === 0) stopRetryTimer();
        }
        return pendingChunksRef.current.length;
    }, [stopRetryTimer]);

    const ensureRetryTimer = useCallback(() => {
        if (retryTimerRef.current) return;
        retryTimerRef.current = setInterval(() => {
            const sessionId = sessionIdRef.current;
            if (!sessionId || pendingChunksRef.current.length === 0) {
                stopRetryTimer();
                return;
            }
            void flushPendingChunks(sessionId);
        }, CHUNK_RETRY_INTERVAL_MS);
    }, [flushPendingChunks, stopRetryTimer]);

    // Handle chunk upload from recorder. The recorder fires this without
    // awaiting it, so the upload promise is registered in
    // inFlightUploadsRef for stopRecording to await before finalize.
    const handleRecorderChunk = useCallback((chunk: AudioChunk) => {
        const sessionId = sessionIdRef.current;
        if (!sessionId) {
            console.error('[MeetingContext] No session ID for chunk upload');
            return;
        }

        console.log(`[MeetingContext] Uploading chunk ${chunk.chunkIndex}, size: ${chunk.blob.size}`);

        const upload = (async () => {
            try {
                const response = await apiUploadMeetingChunk(sessionId, chunk.chunkIndex, chunk.blob);

                if (response.uploaded) {
                    setUploadedChunks(response.total_uploaded);
                    // We don't need to manually increment chunk index here as the recorder manages it
                }
            } catch (err) {
                console.error(`[MeetingContext] Error uploading chunk ${chunk.chunkIndex}, queueing for retry:`, err);
                pendingChunksRef.current = [
                    ...pendingChunksRef.current,
                    { chunkIndex: chunk.chunkIndex, blob: chunk.blob, attempts: 1 },
                ];
                setPendingUploadCount(pendingChunksRef.current.length);
                ensureRetryTimer();
            }
        })();
        inFlightUploadsRef.current.add(upload);
        void upload.finally(() => inFlightUploadsRef.current.delete(upload));
    }, [ensureRetryTimer]);

    // Start recording
    const startRecording = useCallback(async (title?: string, includeSystemAudio: boolean = false): Promise<string | null> => {
        // Ref-based guard: a double-click lands before React re-renders,
        // and a second recorder would leak the first one's mic stream.
        if (recordingStateRef.current !== 'idle') {
            console.warn('[MeetingContext] startRecording ignored — already', recordingStateRef.current);
            return null;
        }

        try {
            setError(null);
            setRecordingDuration(0); // Reset immediately to prevent UI flicker
            setRecState('starting');

            // Create a new session for recording
            const sessionId = await createSession(title || 'New Recording');

            if (!sessionId) {
                throw new Error('Failed to create recording session');
            }

            setCurrentSessionId(sessionId);
            sessionIdRef.current = sessionId;
            setUploadedChunks(0);
            pendingChunksRef.current = [];
            inFlightUploadsRef.current = new Set();
            setPendingUploadCount(0);
            setSilenceWarning(false);
            setSystemAudioUnavailable(false);
            setMicUnavailable(false);
            accumulatedActiveMsRef.current = 0;

            // Initialize and start recorder
            const recorder = new MeetingAudioRecorder(CHUNK_INTERVAL_MS, SILENCE_WARNING_MS);
            recorderRef.current = recorder;
            recorder.setOnSilence(() => setSilenceWarning(true));

            // A source dying mid-recording is the same failure as one
            // that never started, just later: say it out loud, and let
            // the recorder UI drop the silence auto-stop when it is the
            // remote party that went missing.
            recorder.setOnCaptureLoss((kind) => {
                if (kind === 'system_audio') {
                    setSystemAudioUnavailable(true);
                    toast.warning(
                        "Screen share ended — from here on only your microphone is recorded, " +
                        "so other participants won't be in the transcript.",
                        { duration: 10000 }
                    );
                } else {
                    setMicUnavailable(true);
                    toast.error(
                        "Microphone stopped — your voice is no longer being recorded. " +
                        "Stop and save, then start a new recording.",
                        { duration: 15000 }
                    );
                }
            });

            try {
                if (includeSystemAudio) {
                    await recorder.startWithTabAudio(handleRecorderChunk);
                } else {
                    await recorder.start(handleRecorderChunk);
                }

                // startWithTabAudio falls back to mic-only silently when
                // the picker is refused or the chosen share carries no
                // audio track (screen/window shares and the Teams desktop
                // app never do — only a tab with "share tab audio"). The
                // user asked for the remote party and isn't getting them,
                // so say it out loud, and let the UI drop the silence
                // auto-stop: the mic hears nothing while the other side
                // talks, and stopping a live meeting is the worse error.
                const systemAudioMissing =
                    includeSystemAudio && !recorder.isSystemAudioActive();
                setSystemAudioUnavailable(systemAudioMissing);
                if (systemAudioMissing) {
                    toast.warning(
                        "System audio isn't being captured — recording your microphone only. " +
                        "Stop and restart, then share the meeting TAB with \"Also share tab audio\" enabled.",
                        { duration: 10000 }
                    );
                }

                startTimeRef.current = Date.now();
                activeSegmentStartRef.current = Date.now();

                // Live transcript stream (WS-01). Display-only: any
                // failure here leaves the chunk-upload recording path
                // completely untouched. The tap forwards the recorder's
                // merged bus; the client re-slices each frame into the
                // ~100 ms audio messages Transcribe streams reliably.
                try {
                    transcriptStreamClient.reset();
                    void transcriptStreamClient.start(
                        sessionId,
                        // Active time only — the same clock the duration
                        // and the recorded audio use. Wall-clock here
                        // would push every post-pause timestamp ahead of
                        // the audio by the length of the pause.
                        getActiveDurationMs
                    );
                    recorder.setPcmTap((pcm) => transcriptStreamClient.sendPcm(pcm));
                } catch (liveErr) {
                    console.error("[MeetingContext] Live transcript unavailable (recording unaffected):", liveErr);
                }

                setIsRecording(true);
                setRecState('recording');

                // Start timer updates for duration (active time only)
                startDurationTimer();

                return sessionId;
            } catch (recorderErr) {
                console.error("Failed to start audio recorder:", recorderErr);
                transcriptStreamClient.abort();
                // Cleanup if recorder fails start
                await apiDeleteMeetingSession(sessionId);
                setCurrentSessionId(null);
                sessionIdRef.current = null;
                setRecState('idle');
                throw recorderErr;
            }

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to start recording';
            console.error("Failed to start recording:", err);
            setError({ message: errorMessage });
            toast.error(errorMessage);
            setRecState('idle');
            return null;
        }
    }, [createSession, getActiveDurationMs, handleRecorderChunk, setRecState, startDurationTimer]);

    // Pause recording: audio capture stops (the gap simply won't exist
    // in the recorded file), buffered audio is flushed as a chunk, and
    // the duration clock freezes.
    const pauseRecording = useCallback(async () => {
        if (recordingStateRef.current !== 'recording') return;
        const recorder = recorderRef.current;
        if (!recorder) return;

        accumulatedActiveMsRef.current += Date.now() - activeSegmentStartRef.current;
        setRecState('paused');
        setSilenceWarning(false); // pausing IS the answer to "still there?"
        stopDurationTimer();
        setRecordingDuration(Math.floor(accumulatedActiveMsRef.current / 1000));

        await recorder.pause();

        // Transcribe ends a stream ~15 s after the last audio event, so a
        // parked-open socket doesn't survive a pause — it dies as a
        // failure and drags the client into a reconnect loop. Close it
        // cleanly here; resume reopens. Display-only, so failure is
        // harmless.
        await transcriptStreamClient.pause().catch(() => { });
    }, [setRecState, stopDurationTimer]);

    const resumeRecording = useCallback(async () => {
        if (recordingStateRef.current !== 'paused') return;
        const recorder = recorderRef.current;
        if (!recorder) return;

        await recorder.resume();

        activeSegmentStartRef.current = Date.now();
        setRecState('recording');
        startDurationTimer();

        // Reopen the stream pauseRecording closed. Not awaited: the
        // recording must not wait on a token refresh + backfill, and the
        // first second of live text is expendable (display-only).
        void transcriptStreamClient.resume().catch(() => { });
        if (transcriptStreamClient.getStatus() === 'interrupted') {
            void transcriptStreamClient.reconnectNow().catch(() => { });
        }
    }, [setRecState, startDurationTimer]);

    // Stop recording. Returns true if the session was finalized
    // successfully — callers must not claim the recording is saved
    // unless this returns true.
    const stopRecording = useCallback(async (durationSeconds: number): Promise<boolean> => {
        const sessionId = sessionIdRef.current;
        const recorder = recorderRef.current;

        if (!sessionId) {
            console.error("No active recording session");
            return false;
        }

        // Prevent double calling (ref-based: survives stale renders)
        if (recordingStateRef.current === 'stopping' || recordingStateRef.current === 'finalizing') {
            return false;
        }

        try {
            // Freeze the active-duration clock before changing state
            if (recordingStateRef.current === 'recording') {
                accumulatedActiveMsRef.current += Date.now() - activeSegmentStartRef.current;
            }
            setRecState('stopping');
            setError(null);
            setSilenceWarning(false);
            setSystemAudioUnavailable(false);
            setMicUnavailable(false);

            // Stop timer
            stopDurationTimer();

            // Detach the live tap first — no frames after this point.
            if (recorder) {
                recorder.setPcmTap(null);
            }

            // Stop recorder and get final chunk
            let finalChunk: AudioChunk | null = null;
            if (recorder) {
                try {
                    finalChunk = await recorder.stop();
                } catch (err) {
                    console.error("Error stopping recorder:", err);
                }
            }

            // Close the live transcript stream BEFORE finalize: the
            // server drains the last utterance and writes the live
            // transcript during this exchange, and finalize reads it to
            // decide whether the batch job can be skipped. Bounded
            // internally; never blocks the stop on failure.
            try {
                await transcriptStreamClient.stop();
            } catch (liveErr) {
                console.error("[MeetingContext] Live transcript stop error (continuing):", liveErr);
            }

            // Upload final chunk if it exists; failures join the retry queue
            if (finalChunk && finalChunk.blob.size > 44) {
                try {
                    const response = await apiUploadMeetingChunk(sessionId, finalChunk.chunkIndex, finalChunk.blob);
                    if (response.uploaded) {
                        setUploadedChunks(response.total_uploaded);
                    }
                } catch (err) {
                    console.error("Error uploading final chunk, queueing for retry:", err);
                    pendingChunksRef.current = [
                        ...pendingChunksRef.current,
                        { chunkIndex: finalChunk.chunkIndex, blob: finalChunk.blob, attempts: 1 },
                    ];
                    setPendingUploadCount(pendingChunksRef.current.length);
                }
            }

            setRecState('finalizing');

            // Wait out timer-fired uploads still on the wire — a chunk
            // that is mid-upload is not lost, and finalizing under it
            // 409s on a gap that closes itself a second later. Failures
            // land in the pending queue via their own catch.
            if (inFlightUploadsRef.current.size > 0) {
                await Promise.allSettled([...inFlightUploadsRef.current]);
            }

            // Last chance for chunks that failed during the session: two
            // flush passes before finalize, then be honest about any loss.
            stopRetryTimer();
            if (pendingChunksRef.current.length > 0) {
                await flushPendingChunks(sessionId);
            }
            if (pendingChunksRef.current.length > 0) {
                await flushPendingChunks(sessionId);
            }
            const lostChunks = pendingChunksRef.current.length;
            if (lostChunks > 0) {
                const lostSeconds = lostChunks * Math.round(CHUNK_INTERVAL_MS / 1000);
                toast.error(
                    `About ${lostSeconds} seconds of audio could not be uploaded and may be missing from the recording.`,
                    { duration: 10000 }
                );
                console.error(`[MeetingContext] ${lostChunks} chunk(s) permanently failed to upload:`,
                    pendingChunksRef.current.map(p => p.chunkIndex));
            }

            // Calculate final duration if passed 0/undefined, or use the
            // tracked active time (excludes pauses, matches the audio)
            const finalDuration = durationSeconds > 0
                ? durationSeconds
                : Math.floor(accumulatedActiveMsRef.current / 1000);

            // total_chunks for finalize: the recorder's next chunk index.
            // No recorder means no chunks were ever produced.
            const finalChunkCount = recorder ? recorder.getCurrentChunkIndex() : 0;

            try {
                await apiFinalizeMeetingSession(sessionId, finalChunkCount, finalDuration);
            } catch (finalizeErr) {
                // 409 = the server's S3 listing has gaps. It dropped the
                // session back to 'uploading' so re-uploading is allowed:
                // flush whatever we still hold and try once more. A
                // second 409 (chunks truly lost) falls through to the
                // outer catch and the retryable failure state.
                const response = (finalizeErr as any)?.response;
                const missingChunks = response?.status === 409
                    ? response?.data?.detail?.missing_chunks
                    : null;
                if (!missingChunks) throw finalizeErr;
                console.warn('[MeetingContext] Finalize reported missing chunks, re-flushing and retrying:', missingChunks);
                await flushPendingChunks(sessionId);
                await apiFinalizeMeetingSession(sessionId, finalChunkCount, finalDuration);
            }

            setIsRecording(false);
            setRecState('idle');
            setRecordingDuration(finalDuration);
            sessionIdRef.current = null; // Clear the ref
            recorderRef.current = null;
            pendingChunksRef.current = [];
            setPendingUploadCount(0);

            // Refresh the session to get updated status
            await refreshSession(sessionId);

            // Add to meeting notification tracker for the bell icon
            const session = sessions.find(s => s.id === sessionId);
            addMeetingNotification(sessionId, session?.title || 'New Recording');

            toast.success("Recording finalized! Transcription is being processed...");
            return true;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to finalize recording';
            console.error("Failed to finalize recording:", err);
            setError({ message: errorMessage });
            toast.error("Saving failed — your audio is safe. Use Retry saving.");
            // Capture is already torn down, but the session and its
            // uploaded chunks are intact server-side. Keep sessionIdRef/
            // recorderRef so calling stopRecording again retries the
            // upload+finalize half (the recorder steps are no-ops on a
            // stopped recorder). 'finalize_failed' also blocks
            // startRecording, which would orphan this session.
            setIsRecording(false);
            setRecState('finalize_failed');
            return false;
        }
    }, [refreshSession, sessions, setRecState, stopDurationTimer, stopRetryTimer, flushPendingChunks]);

    // Discard the current recording: stop capture, drop everything, and
    // delete the backend session. Nothing is kept.
    const discardRecording = useCallback(async () => {
        const sessionId = sessionIdRef.current;
        const recorder = recorderRef.current;

        if (recordingStateRef.current === 'stopping' || recordingStateRef.current === 'finalizing') {
            return;
        }

        setRecState('stopping');
        setSilenceWarning(false);
        setSystemAudioUnavailable(false);
        setMicUnavailable(false);
        stopDurationTimer();
        stopRetryTimer();

        if (recorder) {
            recorder.setPcmTap(null);
            try {
                await recorder.stop();
            } catch (err) {
                console.error("Error stopping recorder during discard:", err);
            }
        }

        transcriptStreamClient.abort();

        if (sessionId) {
            try {
                await apiDeleteMeetingSession(sessionId);
            } catch (err) {
                console.error("Error deleting discarded session:", err);
            }
        }

        setIsRecording(false);
        setRecState('idle');
        setRecordingDuration(0);
        sessionIdRef.current = null;
        recorderRef.current = null;
        setCurrentSessionId(null);
        pendingChunksRef.current = [];
        inFlightUploadsRef.current = new Set();
        setPendingUploadCount(0);
        accumulatedActiveMsRef.current = 0;

        toast.info("Recording discarded");
    }, [setRecState, stopDurationTimer, stopRetryTimer]);

    // Poll transcription status
    const pollTranscriptionStatus = useCallback(async (
        sessionId: string
    ): Promise<TranscriptionStatusResponse> => {
        try {
            return await apiGetTranscriptionStatus(sessionId);
        } catch (err) {
            console.error("Failed to get transcription status:", err);
            throw err;
        }
    }, []);

    // Get transcription
    const getTranscriptionData = useCallback(async (
        sessionId: string
    ): Promise<Transcription | null> => {
        try {
            return await apiGetTranscription(sessionId);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to get transcription';
            console.error("Failed to get transcription:", err);
            setError({ message: errorMessage });
            toast.error(errorMessage);
            return null;
        }
    }, []);

    // Update speaker mapping
    const updateSpeakerMappingData = useCallback(async (
        sessionId: string,
        mappings: SpeakerMapping[]
    ) => {
        try {
            setLoading(true);
            setError(null);

            await apiUpdateSpeakerMapping(sessionId, mappings);

            // Refresh the session
            await refreshSession(sessionId);

            toast.success("Speaker mapping updated!");
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update speaker mapping';
            console.error("Failed to update speaker mapping:", err);
            setError({ message: errorMessage });
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [refreshSession]);

    // Get download URL
    const getDownloadUrl = useCallback(async (
        sessionId: string
    ): Promise<string | null> => {
        try {
            const response = await apiGetMeetingDownloadUrl(sessionId);
            return response.presigned_url;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to get download URL';
            console.error("Failed to get download URL:", err);
            setError({ message: errorMessage });
            toast.error(errorMessage);
            return null;
        }
    }, []);

    // Retry transcription for stuck/failed sessions; regenerate=true
    // re-runs it for a completed session (replaces the transcript).
    const retryTranscription = useCallback(async (sessionId: string, regenerate: boolean = false) => {
        try {
            setError(null);
            await apiRetryMeetingTranscription(sessionId, regenerate);
            toast.success(regenerate ? "Re-transcription started!" : "Transcription retry started!");
            await refreshSession(sessionId);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to retry transcription';
            console.error("Failed to retry transcription:", err);
            setError({ message: errorMessage });
            toast.error(errorMessage);
        }
    }, [refreshSession]);

    // Set page: a single fetch is the only path — the response updates
    // `pagination`. (There is deliberately no fetch-on-page-change
    // effect: it doubled every pagination request, and its mount run
    // fetched sessions app-wide even when the meetings UI never opened.
    // MeetingManagement owns the initial fetch.)
    const setPage = useCallback((page: number) => {
        void fetchSessions(page);
    }, [fetchSessions]);

    // Warn before the tab closes/refreshes while a recording is live —
    // otherwise the final chunk and finalize never happen and the
    // session is orphaned in "recording" status.
    useEffect(() => {
        const active = recordingState === 'starting'
            || recordingState === 'recording'
            || recordingState === 'paused'
            || recordingState === 'stopping'
            || recordingState === 'finalizing'
            || recordingState === 'finalize_failed';
        if (!active) return;

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            // Chrome requires returnValue to be set to show the prompt
            e.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [recordingState]);

    // Background poll for transcription completion - updates bell notifications
    useEffect(() => {
        if (!userId) return;

        const pollMeetingNotifications = async () => {
            // Nobody is looking; the next visible tick catches up.
            if (document.hidden) return;

            const pendingNotifications = getMeetingNotifications().filter(
                n => n.status === 'processing' || n.status === 'transcribing'
            );

            if (pendingNotifications.length === 0) return;

            for (const notification of pendingNotifications) {
                // Stop polling sessions stuck longer than the views'
                // own "stuck" threshold — mark them failed instead of
                // hitting the API every tick forever.
                const age = Date.now() - new Date(notification.created_at).getTime();
                if (age > NOTIFICATION_MAX_AGE_MS) {
                    updateMeetingNotificationStatus(notification.sessionId, 'failed');
                    continue;
                }

                try {
                    const statusResponse = await apiGetTranscriptionStatus(notification.sessionId);

                    if (statusResponse.status === 'COMPLETED') {
                        updateMeetingNotificationStatus(notification.sessionId, 'completed');
                        // Refresh sessions list to reflect the new status
                        await refreshSession(notification.sessionId);
                    } else if (statusResponse.status === 'FAILED') {
                        updateMeetingNotificationStatus(notification.sessionId, 'failed');
                    } else if (statusResponse.status === 'IN_PROGRESS') {
                        updateMeetingNotificationStatus(notification.sessionId, 'transcribing');
                    }
                } catch (err) {
                    // A deleted session would otherwise be polled forever.
                    if ((err as any)?.response?.status === 404) {
                        removeMeetingNotification(notification.sessionId);
                        continue;
                    }
                    console.error(`Error polling transcription for ${notification.sessionId}:`, err);
                }
            }
        };

        const intervalId = setInterval(pollMeetingNotifications, NOTIFICATION_POLL_INTERVAL_MS);
        // Also poll immediately on mount
        pollMeetingNotifications();

        return () => clearInterval(intervalId);
    }, [userId, refreshSession]);

    const meetingValue = useMemo<MeetingContextProps>(() => ({
        sessions,
        selectedSession,
        loading,
        error,
        pagination,
        setSelectedSession,
        fetchSessions,
        createSession,
        updateSession,
        deleteSession,
        pollTranscriptionStatus,
        getTranscription: getTranscriptionData,
        updateSpeakerMapping: updateSpeakerMappingData,
        getDownloadUrl,
        setPage,
        resetError,
        refreshSession,
        retryTranscription,
    }), [
        sessions,
        selectedSession,
        loading,
        error,
        pagination,
        fetchSessions,
        createSession,
        updateSession,
        deleteSession,
        pollTranscriptionStatus,
        getTranscriptionData,
        updateSpeakerMappingData,
        getDownloadUrl,
        setPage,
        resetError,
        refreshSession,
        retryTranscription,
    ]);

    const recordingValue = useMemo<MeetingRecordingContextProps>(() => ({
        isRecording,
        recordingState,
        currentSessionId,
        recordingDuration,
        uploadedChunks,
        pendingUploadCount,
        silenceWarning,
        systemAudioUnavailable,
        micUnavailable,
        startRecording,
        dismissSilenceWarning,
        pauseRecording,
        resumeRecording,
        stopRecording,
        discardRecording,
    }), [
        isRecording,
        recordingState,
        currentSessionId,
        recordingDuration,
        uploadedChunks,
        pendingUploadCount,
        silenceWarning,
        systemAudioUnavailable,
        micUnavailable,
        startRecording,
        dismissSilenceWarning,
        pauseRecording,
        resumeRecording,
        stopRecording,
        discardRecording,
    ]);

    return (
        <MeetingContext.Provider value={meetingValue}>
            <MeetingRecordingContext.Provider value={recordingValue}>
                {children}
            </MeetingRecordingContext.Provider>
        </MeetingContext.Provider>
    );
};

export default MeetingProvider;
